import { useCallback, useRef, useState } from 'react'
import { buildCrmIntegrationVoiceFile } from '../lib/crmIntegrationVoiceFile'

export type UseCrmIntegrationVoiceRecorderResult = {
  isRecording: boolean
  audioURL: string
  audioFile: File | null
  startRecording: () => Promise<void>
  stopRecording: () => void
  resetRecording: () => void
  cleanup: () => void
  hasAudio: boolean
}

export const useCrmIntegrationVoiceRecorder =
  (): UseCrmIntegrationVoiceRecorderResult => {
    const [isRecording, setIsRecording] = useState(false)
    const [audioURL, setAudioURL] = useState('')
    const [audioFile, setAudioFile] = useState<File | null>(null)

    const mediaRecorderRef = useRef<MediaRecorder | null>(null)
    const audioChunksRef = useRef<Blob[]>([])
    const streamRef = useRef<MediaStream | null>(null)
    const recordedMimeTypeRef = useRef<string>('')

    const stopMediaStream = () => {
      streamRef.current?.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }

    const startRecording = useCallback(async () => {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      audioChunksRef.current = []

      const mediaRecorder = new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder
      recordedMimeTypeRef.current = mediaRecorder.mimeType || ''

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = () => {
        const recordedMimeType =
          recordedMimeTypeRef.current ||
          mediaRecorder.mimeType ||
          audioChunksRef.current[0]?.type ||
          'audio/webm'
        const recordedBlob = new Blob(audioChunksRef.current, {
          type: recordedMimeType,
        })
        const file = buildCrmIntegrationVoiceFile(recordedBlob, recordedMimeType)
        const previewUrl = URL.createObjectURL(recordedBlob)

        setAudioFile(file)
        setAudioURL(previewUrl)
        audioChunksRef.current = []
        stopMediaStream()
      }

      mediaRecorder.start(100)
      setIsRecording(true)
      setAudioURL('')
      setAudioFile(null)
    }, [])

    const stopRecording = useCallback(() => {
      const recorder = mediaRecorderRef.current
      if (recorder && recorder.state !== 'inactive') {
        recorder.stop()
      }
      setIsRecording(false)
    }, [])

    const resetRecording = useCallback(() => {
      if (isRecording) {
        stopRecording()
      }

      if (audioURL.startsWith('blob:')) {
        URL.revokeObjectURL(audioURL)
      }

      stopMediaStream()
      setAudioURL('')
      setAudioFile(null)
      audioChunksRef.current = []
      recordedMimeTypeRef.current = ''
    }, [audioURL, isRecording, stopRecording])

    const cleanup = useCallback(() => {
      if (audioURL.startsWith('blob:')) {
        URL.revokeObjectURL(audioURL)
      }
      stopMediaStream()
    }, [audioURL])

    return {
      isRecording,
      audioURL,
      audioFile,
      startRecording,
      stopRecording,
      resetRecording,
      cleanup,
      hasAudio: Boolean(audioURL),
    }
  }
